import { Container, View } from "@amzn/stencil-react-components/layout";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ProductDetailsPage, ProductDetailsPageProps } from "common/components/ProductDetailsPage";
import { TrafficFrom } from "common/helpers/traffic-from";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useGetItemDetailsFactory } from "./useGetItemDetailsFactory";

const DetailsPage = () => {
  const history = useHistory();
  // Gets the Taxonomy parts from the url location
  const location = useLocation();
  const taxonomyUrl = location.pathname.replace("/items/", "");

  checkIfExternal();

  const atn = ItemTaxonomyId.ParseWithoutTypePrefix(taxonomyUrl);
  const { addErrorBanner } = useMessageBannersBox();

  if (atn.dataDomain === "itsoftware") {
    // Do a redirect, in case someone bookmarked a url with itsoftware
    window.location.pathname = window.location.pathname.replace("itsoftware", "software");
  }

  // Check permissions on component mount
  useEffect(() => {
    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (
      (atn.dataDomain === "hardware" || atn.dataDomain === "amznham") &&
      !UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP)
    ) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, [atn.dataDomain]);

  const useGetItemDetails = useGetItemDetailsFactory(atn);

  const {
    type,
    item,
    isLoading,
    error,
    defaultVersion,
    versions,
    preCheckoutData,
    getItem,
    updateVersionSelected,
    fetchPreCheckoutData,
  } = useGetItemDetails();

  // Used to be able to load data async
  useEffect(() => {
    getItem(atn);
  }, [taxonomyUrl]);

  useEffect(() => {
    if (error) {
      const errorMessage = error.message;
      addErrorBanner(getString("errors.general") + ` Error: ${errorMessage}. `);
    }
  }, [error]);

  if (item && !isLoading) {
    const pdpProps: ProductDetailsPageProps = {
      assetType: type,
      item: item,
      defaultVersion: defaultVersion,
      versions: versions,
      preCheckoutInformation: preCheckoutData,
      fetchPreCheckoutData: fetchPreCheckoutData,
      updateVersionSelected: updateVersionSelected,
    };
    return (
      <PageContainer paddingHorizontal={0} paddingTop={0} isFullWidth={true}>
        {/* This is where we will inject the breadcrumb links as child of TopBar, rendering a blank space */}
        <ProductDetailsPage {...pdpProps} />
      </PageContainer>
    );
  } else {
    // No data, either loading or not found
    return (
      <PageContainer paddingHorizontal={0} paddingTop={0} isFullWidth={true}>
        <Container isFullWidth={false}>
          {isLoading && (
            <View>
              <Spinner size={SpinnerSize.Medium} fr={undefined} />
            </View>
          )}
        </Container>
      </PageContainer>
    );
  }

  function checkIfExternal() {
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get("from");
    if (!from) {
      history.push({
        pathname: location.pathname,
        search: `?from=${TrafficFrom.External}`,
      });
    }
  }
};

export default DetailsPage;
